import React from 'react';

/**
 * Why this device can or cannot receive Web Push, in the terms the UI needs.
 *
 * Feature detection alone cannot distinguish "this browser will never do
 * push" from "this iPhone will do push the moment you add the app to the
 * Home Screen" — both look like a missing `PushManager`. That distinction is
 * the whole point of this type: one state is a dead end, the other is a
 * one-tap fix the operator can perform themselves.
 */
export type PushCapability =
  /** Push API present; proceed with the normal permission/subscribe flow. */
  | 'available'
  /** iOS/iPadOS in a browser tab — installing to the Home Screen fixes it. */
  | 'needs-install'
  /** Installed on iOS, but the OS predates Web Push (iOS < 16.4). */
  | 'needs-ios-update'
  /** Genuinely no Push API and no remedy the user can apply. */
  | 'unsupported';

const hasPushApi = (): boolean =>
  typeof navigator !== 'undefined' &&
  'serviceWorker' in navigator &&
  typeof window !== 'undefined' &&
  'PushManager' in window;

/**
 * "Is this an iPhone or iPad", independent of whether the app is installed.
 *
 * Deliberately NOT `isIOS()` from `usePWAInstall.tsx`: that helper returns
 * `false` once `navigator.standalone` is set, because it answers a different
 * question ("should we still nag about installing?"). Reusing it here would
 * make the `needs-ios-update` branch below unreachable, since that branch is
 * defined precisely by *being* standalone.
 */
const isIOSDevice = (): boolean => {
  if (typeof window === 'undefined') return false;
  const nav = window.navigator;
  if (/iPad|iPhone|iPod/i.test(nav.userAgent)) return true;
  // iPadOS 13+ reports a desktop Macintosh user agent. Touch points are the
  // only reliable way to tell it apart from a real Mac, and it matters here:
  // an iPad in a Safari tab has no PushManager either, and the same Home
  // Screen install fixes it. A real Mac reports 0 touch points — and modern
  // macOS Safari has the Push API anyway, so it never reaches this check.
  return /Macintosh/i.test(nav.userAgent) && nav.maxTouchPoints > 1;
};

/**
 * "Is this page running as an installed Home Screen app?" Both signals are
 * needed: iOS Safari predates `display-mode` and exposes the non-standard,
 * iOS-only `navigator.standalone` instead — hence the widened DOM type.
 */
const isStandalone = (): boolean => {
  if (typeof window === 'undefined') return false;
  const nav = window.navigator as Navigator & { standalone?: boolean };
  return (
    window.matchMedia?.('(display-mode: standalone)').matches === true ||
    nav.standalone === true
  );
};

/**
 * Pure capability read — no permission prompt, no subscribe, no navigation.
 * Keep it that way: iOS silently rejects permission requests that do not
 * originate from a user gesture, so nothing here may ever grow a side effect.
 *
 * First match wins.
 */
export const detectPushCapability = (): PushCapability => {
  if (hasPushApi()) return 'available';
  if (!isIOSDevice()) return 'unsupported';
  return isStandalone() ? 'needs-ios-update' : 'needs-install';
};

/**
 * The single source of "can we push here". Add future platform quirks here
 * rather than scattering `isIOS()` checks through components.
 */
export const usePushCapability = (): PushCapability =>
  React.useMemo(detectPushCapability, []);
